import React from 'react';
import './App.css';

export default function PrivacyPolicy() {
  return (
    <div className="app-container">
      <h1>Privacy Policy</h1>
      <p><strong>Effective Date:</strong> December 22, 2025</p>

      <section>
        <h2>Introduction</h2>
        <p>
          This Privacy Policy describes how FMC Grader ("we," "us," or "our") collects, uses, 
          stores, and protects information when you use our optical character recognition service 
          for Rubik's Cube FMC (Fewest Moves Challenge) solution transcription.
        </p>
        <p>
          We are committed to protecting your privacy and handling your data with transparency and care. 
          This policy outlines our practices regarding the information we collect through the FMC Grader 
          application and third-party services we utilize.
        </p>
      </section>

      <section>
        <h2>Information We Collect</h2>
        
        <h3>User-Provided Content</h3>
        <ul>
          <li><strong>Images:</strong> When you upload or capture images of FMC solution sheets for transcription, 
          these images are transmitted to our OCR processing infrastructure. Images are processed temporarily 
          in memory and are not persisted to permanent storage on our servers.</li>
          <li><strong>Derived Data:</strong> Text extracted from your images (scrambles, moves, and related 
          cube notation) is processed during your active session to provide the transcription service.</li>
        </ul>

        <h3>Automatically Collected Information</h3>
        <p>
          Our application does not collect analytics, cookies, or tracking data. We do not log IP addresses, 
          browser information, device identifiers, or other diagnostic information on our servers.
        </p>
      </section>

      <section>
        <h2>How We Use Your Information</h2>
        <p>
          The information we collect is used exclusively to:
        </p>
        <ul>
          <li>Provide optical character recognition services for FMC solution transcription</li>
          <li>Process and analyze uploaded images to extract scrambles and move sequences</li>
          <li>Display transcription results within your browser session</li>
          <li>Maintain and improve the quality and accuracy of our service</li>
        </ul>
        <p>
          We do not use your data for marketing, advertising, or any purpose beyond providing 
          the core transcription functionality.
        </p>
      </section>

      <section>
        <h2>Data Storage and Retention</h2>
        <p>
          <strong>Client-Side Processing:</strong> The FMC Grader application operates primarily in your browser. 
          OCR processing is performed through our backend API, which interfaces with third-party OCR providers.
        </p>
        <p>
          <strong>No Persistent Storage:</strong> Our server infrastructure is configured to process images 
          exclusively in memory. We do not write uploaded images or transcription results to disk or maintain 
          any permanent storage of your content.
        </p>
        <p>
          <strong>Session Data:</strong> Transcription results exist only during your active browser session 
          and are cleared when you close the application or refresh the page.
        </p>
      </section>

      <section>
        <h2>Third-Party Services</h2>
        <p>
          To provide OCR functionality, we utilize the following third-party service:
        </p>
        
        <h3>OlmoCR Vision Model</h3>
        <p>
          FMC Grader uses <strong>OlmoCR</strong>, an open-source optical character recognition model 
          developed by the Allen Institute for AI (AI2). OlmoCR is accessed through inference API providers 
          including DeepInfra, Cirrascale, and Parasail.
        </p>
        <p>
          <strong>Data Handling:</strong> When you submit an image for transcription, your image is transmitted 
          to the selected OlmoCR inference provider for processing. These providers process images through their 
          API infrastructure to generate text output.
        </p>
        <p>
          <strong>No Training on User Data:</strong> OlmoCR is a pre-trained model. The inference providers 
          we work with do not use submitted images to train, improve, or modify the model. Your images are 
          processed solely to generate transcription results and are not retained for model development purposes.
        </p>
        <p>
          <strong>Provider Privacy:</strong> Each inference provider maintains their own privacy practices 
          and may collect request metadata (such as timestamps and API usage statistics) in accordance with 
          their terms of service. We recommend reviewing the privacy policies of our providers:
        </p>
        <ul>
          <li><a href="https://deepinfra.com/privacy" target="_blank" rel="noopener noreferrer">DeepInfra Privacy Policy</a></li>
          <li><a href="https://cirrascale.ai/privacy" target="_blank" rel="noopener noreferrer">Cirrascale Privacy Policy</a></li>
          <li><a href="https://parasail.io/privacy" target="_blank" rel="noopener noreferrer">Parasail Privacy Policy</a></li>
        </ul>
      </section>

      <section>
        <h2>Data Sharing and Disclosure</h2>
        <p>
          We do not sell, rent, or trade your personal information or uploaded content to third parties. 
          We may share information only in the following limited circumstances:
        </p>
        <ul>
          <li><strong>Service Providers:</strong> As described above, images are transmitted to OCR inference 
          providers for processing purposes only</li>
          <li><strong>Legal Requirements:</strong> If required by law, court order, or governmental regulation, 
          we may disclose information to comply with legal obligations</li>
          <li><strong>Protection of Rights:</strong> We may disclose information when necessary to protect 
          our rights, property, or safety, or that of our users or others</li>
        </ul>
      </section>

      <section>
        <h2>Data Security</h2>
        <p>
          We implement industry-standard security measures to protect data during transmission and processing:
        </p>
        <ul>
          <li>All data transmission between your browser and our servers uses encrypted HTTPS connections</li>
          <li>Communications with third-party OCR providers are secured using TLS encryption</li>
          <li>API keys and credentials are stored securely using environment variables and are never exposed to clients</li>
          <li>Our infrastructure is configured to process images in memory without persistent storage</li>
        </ul>
        <p>
          While we take reasonable measures to protect your information, no system can be completely secure. 
          We cannot guarantee the absolute security of data transmitted over the internet.
        </p>
      </section>

      <section>
        <h2>Your Rights and Choices</h2>
        <p>
          Because we do not maintain persistent storage of your images or transcription results, there is 
          no personal data to access, modify, or delete from our systems after your session ends.
        </p>
        <p>
          <strong>Control Over Your Data:</strong> You have complete control over what images you choose to 
          upload. You may discontinue use of the service at any time.
        </p>
        <p>
          <strong>Questions or Concerns:</strong> If you have questions about our privacy practices or wish 
          to exercise any rights under applicable privacy laws, please contact us through our 
          <a href="https://github.com/Sixstringcal/FMC-Grader" target="_blank" rel="noopener noreferrer"> GitHub repository</a>.
        </p>
      </section>

      <section>
        <h2>Children's Privacy</h2>
        <p>
          FMC Grader is not directed to individuals under the age of 13. We do not knowingly collect 
          personal information from children. If you believe a child has provided us with personal information, 
          please contact us so we can take appropriate action.
        </p>
      </section>

      <section>
        <h2>International Users</h2>
        <p>
          FMC Grader may be accessed from countries around the world. By using our service, you understand 
          that your data may be transmitted to and processed in the United States and other jurisdictions 
          where our service providers operate, which may have different data protection laws than your country.
        </p>
      </section>

      <section>
        <h2>Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or for 
          legal or regulatory reasons. When we make changes, we will update the "Effective Date" at the 
          top of this policy. We encourage you to review this policy periodically.
        </p>
        <p>
          Material changes to how we handle your information will be communicated through a notice on 
          our application or other appropriate channels.
        </p>
      </section>

      <section>
        <h2>Contact Information</h2>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
          please reach out to us:
        </p>
        <ul>
          <li><strong>Project Repository:</strong> <a href="https://github.com/Sixstringcal/FMC-Grader" target="_blank" rel="noopener noreferrer">https://github.com/Sixstringcal/FMC-Grader</a></li>
          <li><strong>Issues and Support:</strong> Please submit inquiries through GitHub Issues</li>
        </ul>
      </section>

      <p style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid #ddd' }}>
        <a href="/" style={{ fontSize: '1.1em' }}>← Return to FMC Grader</a>
      </p>
    </div>
  );
}
